import React, {ReactNode} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {TranslatedLOVItem} from '@findhotel/sapi'
import {useApSeoParentPlace} from 'hooks/useApSeoBreadcrumb'
import {getSeoActiveAttributes} from 'modules/meta/selectors'
import {LovHit} from 'modules/search/slice'
import {getFacilitySeoSlug} from 'utils/server/searchSeo/getSlugFromFilters'

import {Link} from '@vio/atlas/Link'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'

import {FacilityIcon} from './FacilityIcon'

type RowGap = 's300' | 's400' | 's500'
export type Columns = 1 | 2 | 3

interface Props {
  facilities: LovHit[] | TranslatedLOVItem[]
  columns?: Columns
  rowGap?: RowGap
}

const FacilitiesList = styled.ul<{$columns: Columns; $rowGap: RowGap}>`
  display: grid;
  grid-template-columns: ${({$columns}) =>
    $columns === 1 ? '1fr' : '1fr 1fr'};
  gap: ${cssTheme.layout.spacing.s300};
  margin: 0;
  padding: 0;
  list-style: none;

  ${linariaMq.desktopXs} {
    grid-template-columns: ${({$columns}) =>
      $columns === 3 ? '1fr 1fr 1fr' : $columns === 1 ? '1fr' : '1fr 1fr'};
    column-gap: ${cssTheme.layout.spacing.s500};
    row-gap: ${({$rowGap}) => cssTheme.layout.spacing[$rowGap]};
  }
`

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s300};
  ${linariaMq.desktopXs} {
    align-items: flex-start;
  }
`

const IconContainer = styled.span`
  ${linariaMq.desktopXs} {
    margin-top: 3px;
  }
`

const FacilitiesGridLayout: React.FC<{
  children: ReactNode[]
  columns?: Columns
  rowGap?: RowGap
}> = ({children, columns = 3, rowGap = 's500'}) => {
  const childrenArray = React.Children.toArray(children)
  return (
    <FacilitiesList $columns={columns} $rowGap={rowGap}>
      {childrenArray}
    </FacilitiesList>
  )
}

const SECONDARY_LOADER_WIDTHS = [104, 123, 56]

export const FacilitiesGridLoader = ({
  items = 6,
  variant = 'primary',
  columns = 3,
  rowGap
}: {
  items?: number
  variant?: 'primary' | 'secondary'
  columns?: Columns
  rowGap?: RowGap
}) => {
  return (
    <FacilitiesGridLayout columns={columns} rowGap={rowGap}>
      {new Array(items).fill('').map((_, index) =>
        variant === 'secondary' ? (
          <li key={index}>
            <SkeletonLoader
              width={
                SECONDARY_LOADER_WIDTHS[
                  Math.floor(index / columns) % SECONDARY_LOADER_WIDTHS.length
                ]
              }
            />
          </li>
        ) : (
          <Item key={index}>
            <SkeletonLoader width={80} marginTop={0} />
          </Item>
        )
      )}
    </FacilitiesGridLayout>
  )
}

export const FacilitiesGrid: React.FC<Props> = ({
  facilities,
  columns,
  rowGap
}: Props) => {
  const seoAttributes = useSelector(getSeoActiveAttributes)
  const seoParentPlace = useApSeoParentPlace()

  return (
    <FacilitiesGridLayout columns={columns} rowGap={rowGap}>
      {facilities?.map(facility => {
        const slug = seoParentPlace
          ? getFacilitySeoSlug(facility.id, seoAttributes)
          : null
        const href = slug ? `${seoParentPlace.path}/f/${slug}` : null

        return (
          <Item key={facility.id}>
            <IconContainer>
              <FacilityIcon facility={facility} />
            </IconContainer>
            {href ? (
              <Link href={href}>
                <Text variant="bodyM">{facility.value}</Text>
              </Link>
            ) : (
              <Text variant="bodyM">{facility.value}</Text>
            )}
          </Item>
        )
      })}
    </FacilitiesGridLayout>
  )
}
